<template>
  <v-container
    fluid
  >
    <v-row>
      <v-col>
        <h2>
          <v-icon class="mr-2">
            mdi-point-of-sale
          </v-icon>
          売上管理
        </h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-expansion-panels
          accordion
        >
          <v-expansion-panel>
            <v-expansion-panel-header class="panel-header-color panel-selectable">
              <strong>
                <v-icon class="panel-header-color">
                  mdi-magnify
                </v-icon>
                &nbsp;条件を指定して検索
              </strong>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card flat>
                <v-card-text>

                  <!-- ステータス, 取引日（From）, 取引日（To）, 現掛区分, 営業担当者 -->
                  <v-row>
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="conditions.status"
                        :items="status"
                        class="search-font"
                        dense
                        hide-details="auto"
                        label="ステータス"
                        clearable
                        outlined
                        persistent-placeholder
                        rounded
                        @change="searchButtonClickEvent"
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <cs-date-picker
                        :date.sync="conditions.trading_start_date"
                        @dateChangedCallback="tradingStartDateChangedCallback"
                      >
                        <template v-slot="{ on, attrs }">
                          <v-text-field
                            v-model="conditions.trading_start_date"
                            v-bind="attrs"
                            v-on="on"
                            append-icon="mdi-calendar"
                            clearable
                            dense
                            hide-details="auto"
                            label="取引日（From）"
                            outlined
                            persistent-placeholder
                            placeholder="日付を選択"
                            readonly
                            rounded
                            style="font-size: 80%"
                            @click:clear="tradingStartDateClearEvent"
                          >
                            <template v-slot:append-outer>
                              <div
                                v-if="$vuetify.breakpoint.mdAndUp"
                                style="vertical-align: middle"
                              >
                                〜
                              </div>
                            </template>
                          </v-text-field>
                        </template>
                      </cs-date-picker>
                    </v-col>
                    <v-col cols="12" md="2">
                      <cs-date-picker
                        :date.sync="conditions.trading_end_date"
                        @dateChangedCallback="tradingEndDateChangedCallback"
                      >
                        <template v-slot="{ on, attrs }">
                          <v-text-field
                            v-model="conditions.trading_end_date"
                            v-bind="attrs"
                            v-on="on"
                            append-icon="mdi-calendar"
                            clearable
                            dense
                            hide-details="auto"
                            label="取引日（To）"
                            outlined
                            persistent-placeholder
                            placeholder="日付を選択"
                            readonly
                            rounded
                            style="font-size: 80%"
                            @click:clear="tradingEndDateClearEvent"
                          />
                        </template>
                      </cs-date-picker>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="conditions.receivable_type"
                        :items="receivableTypes"
                        class="search-font"
                        dense
                        hide-details="auto"
                        label="現掛区分"
                        clearable
                        outlined
                        persistent-placeholder
                        rounded
                        @change="searchButtonClickEvent"
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="conditions.sales_staff"
                        class="search-font"
                        background-color="white"
                        dense
                        hide-details="auto"
                        label="営業担当者"
                        outlined
                        rounded
                        persistent-placeholder
                        clearable
                        @blur="searchButtonClickEvent"
                        @click:clear="conditions.sales_staff = null; searchButtonClickEvent()"
                      />
                    </v-col>
                  </v-row>
                  <!-- /ステータス, 取引日（From）, 取引日（To）, 現掛区分, 営業担当者 -->

                  <!-- 取引先（排出）事業者, 取引先（排出）事業場 -->
                  <infinite-emission-and-location-auto-complete
                    :supplier-id="conditions.supplier_id"
                    :location-id="conditions.supplier_location_id"
                    :location-code="conditions.supplier_location_code"
                    classes="search-font"
                    supplier-label="取引先（排出）"
                    location-label="取引先事業場"
                    supplier-md="4"
                    location-md="4"
                    rounded
                    uncontrol-disabled
                    @update:values="supplierChangeCallback"
                  >
                    <template v-slot:supplier-append-outer>
                      <choose-emission-button
                        :button-caption="`取引先を選択`"
                        :small-button="true"
                        @choseEmissionCallback="choseSupplierCallback"
                      />
                    </template>
                  </infinite-emission-and-location-auto-complete>
                  <!-- /取引先（排出）事業者, 取引先（排出）事業場 -->

                  <!-- 取引先（収運・処分）事業者, 取引先（収運・処分）事業場 -->
                  <infinite-trader-and-location-auto-complete
                    :trader-id="conditions.supplier_trader_id"
                    :location-id="conditions.supplier_trader_location_id"
                    :location-code="conditions.supplier_trader_location_code"
                    classes="search-font"
                    trader-label="取引先（収運・処分）"
                    location-label="取引先事業場"
                    trader-md="4"
                    location-md="4"
                    search-type="either"
                    rounded
                    with-none-location
                    @update:values="supplierTraderChangedCallback"
                  >
                    <template v-slot:trader-append-outer>
                      <choose-trader-button
                        :button-caption="`取引先を選択`"
                        small-button
                        with-none-location
                        @choseTraderCallback="choseSupplierTraderCallback"
                      />
                    </template>
                  </infinite-trader-and-location-auto-complete>
                  <!-- /取引先（収運・処分）事業者, 取引先（収運・処分）事業場 -->

                  <!-- 請求先, 取引番号・引取搬入番号・受注番号で検索, 表示順 -->
                  <v-row>
                    <v-col cols="12" md="4">
                      <infinite-billing-auto-complete
                        :billing-id="conditions.billing_id"
                        rounded
                        classes="search-font"
                        label="請求先"
                        @update:values="billingChangedCallback"
                      >
                        choseBillingCallback
                        <template v-slot:append-outer>
                          <choose-billing-address-button
                            :small-button="true"
                            @choseBillingAddressCallback="choseBillingCallback"
                          />
                        </template>
                      </infinite-billing-auto-complete>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="conditions.keyword"
                        prepend-inner-icon="mdi-magnify"
                        background-color="white"
                        dense
                        hide-details="auto"
                        outlined
                        rounded
                        placeholder="取引番号・引取搬入番号・受注番号で検索"
                        style="font-size: 80%"
                        clearable
                        @blur="searchButtonClickEvent"
                        @click:clear="keywordClearEvent"
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="conditions.sort_type"
                        :items="sortTypes"
                        dense
                        hide-details="auto"
                        label="表示順"
                        outlined
                        persistent-placeholder
                        rounded
                        style="font-size: 80%"
                        @change="searchButtonClickEvent"
                      />
                    </v-col>
                  </v-row>
                  <!-- /請求先, 取引番号・引取搬入番号・受注番号で検索, 表示順 -->

                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    @click="searchButtonClickEvent"
                  >
                    <v-icon>
                      mdi-magnify
                    </v-icon>
                    &nbsp;検索
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card-actions>
          <v-spacer />

          <v-btn
            class="info"
            @click="outputCsvButtonClickEvent"
          >
          <v-icon>mdi-file-download-outline</v-icon>&nbsp;CSV出力
          </v-btn>
          &nbsp;&emsp;
          <v-menu
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                class="text-h6"
                dark
                x-large
                v-bind="attrs"
                v-on="on"
              >
                新規作成&nbsp;
                <v-icon large>
                  mdi-plus
                </v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                link
                @click="addButtonClickEvent"
              >
                <v-list-item-title>
                  新規作成
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                link
                @click="showDuplicateDialogButtonClickEvent"
              >
                <v-list-item-title>
                  複写して作成
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-actions>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card flat class="rounded-card">
          <v-data-table
            :headers="tableHeaders"
            :items="deals"
            disable-pagination
            :items-per-page="-1"
            disable-sort
            hide-default-footer
          >
            <!-- ステータス -->
            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                x-small
                label
                dark
                class="stretchedChip"
                :color="item.status | status_chip_transaction_color"
              >
                {{ item.status | status_to_label_transaction }}
              </v-chip>
            </template>
            <!-- ステータス -->

            <!-- 取引番号/受入番号 -->
            <template v-slot:[`item.deal_no`]="{ item }">
              <div>
                <span v-if="item.deal_no">
                  {{ item.deal_no }}
                </span>
                <span v-else>
                  -&nbsp;
                </span>
              </div>
              <div>
                <div v-if="item.order_no" >
                  <v-btn
                    text
                    color="primary"
                    @click="orderNoClickEvent(item)"
                  >
                    {{ item.order_no }}
                  </v-btn>
                </div>
                <div v-else>{{ '-' }}</div>
              </div>
            </template>
            <!-- /取引番号/受入番号 -->
            <template v-slot:[`item.trading_date`]="{item}">
              <div>{{ item.trading_date | date_format }}</div>
              {{ item.sales_date | date_format }}
            </template>
            <!-- 取引先 -->
            <template v-slot:[`item.supplier_name`]="{ item }">
              <div v-if="item.supplier_name">
                <div
                  :title="item.supplier_name"
                  class="line-clamp one-clamp float-left"
                  style="max-width: 85%;width: auto;"
                >
                  {{ item.supplier_name }}
                </div>
                <v-tooltip
                  top
                  :open-on-hover="false"
                >
                  <template #activator="{ on }">
                    <v-btn
                      v-if="item.supplier_name"
                      icon
                      small
                      retain-focus-on-click
                      @click="on.click"
                      @blur="on.blur"
                      class="pb-2"
                    >
                      <v-icon small>
                        mdi-map-marker-outline
                      </v-icon>
                    </v-btn>
                  </template>
                  <div
                    v-if="item.supplier_name"
                  >
                    <strong>{{ item.supplier_name }}</strong>
                    <br />
                    <strong>{{ item.supplier_location_name }}</strong>
                    <br />
                    <small>{{ item.supplier_location_address }}</small>
                  </div>
                </v-tooltip>
              </div>
              <div
                v-if="item.supplier_location_name"
                :title="item.supplier_location_name"
                class="line-clamp one-clamp"
                style="max-width: 100%;width: auto;"
              >
                {{ item.supplier_location_name }}
              </div>
            </template>
            <!-- /取引先 -->
            <!-- 請求先 -->
            <template v-slot:[`item.billing_address`]="{ item }">
              <div
                :title="item.billing_address"
                class="line-clamp one-clamp"
                style="max-width: 100%;width: auto;"
              >
                {{ item.billing_address }}
              </div>
            </template>
            <!-- /請求先 -->
            <template v-slot:[`item.receivable_type`]="{item}">
              {{ item.receivable_type | receivable_type_label }}
            </template>
            <template v-slot:[`item.total_amount`]="{ item }">
              <span class="font-weight-black">
                {{ parseFloat(item.total_amount) | comma_format }}
              </span>
            </template>
            <!-- 詳細 -->
            <template v-slot:[`item.editable`]="{ item }">
              <v-menu
                bottom
                left
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                {{ item.name }}
                <v-list dense>
                  <v-list-item
                    v-if="(item.status === `unregistered`)"
                    link
                    @click="editButtonClickEvent(item)"
                  >
                    <v-list-item-title>
                      受注からの売上登録
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="['provisional', 'registered'].includes(item.status)"
                    link
                    @click="editButtonClickEvent(item)"
                  >
                    <v-list-item-title>
                      編集
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="['unregistered', 'provisional', 'registered'].includes(item.status)"
                    link
                    @click="deleteButtonClickEvent(item)"
                  >
                    <v-list-item-title class="red--text font-weight-bold">
                      <span v-if="item.status === `unregistered`">
                        売上計上しない
                      </span>
                      <span v-else>
                        キャンセル
                      </span>
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="(item.status === `final_closing`)"
                    link
                    @click="showButtonClickEvent(item)"
                  >
                    <v-list-item-title>
                      詳細を参照
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="(item.can_view_voucher)"
                    link
                    @click="viewVoucherPdfButtonClickEvent(item)"
                  >
                    <v-list-item-title>
                      伝票出力
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <!-- /詳細 -->
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- ページャ -->
    <v-row>
      <v-col>
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row>
    <!-- /ページャ -->
    <duplicate-transaction-dialog
      :enable-dialog="enableDuplicateDialog"
      @closeCallback="duplicateDialogCloseCallback"
      @registerCallback="duplicateDialogRegisterCallback"
    />
    <confirm ref="confirm" />
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Confirm from '@/components/Confirm'
import DuplicateTransactionDialog from './dialogs/_DuplicateDialog'
import ChooseBillingAddressButton from "@/driver/components/ChooseBillingAddressButton";
import DownloadMixin from '../../../../mixins/download-mixin'
import CustomPagination from "@/driver/components/_CustomPagination.vue";
import InfiniteEmissionAndLocationAutoComplete from "@/driver/components/InfiniteEmissionAndLocationAutoComplete.vue";
import InfiniteTraderAndLocationAutoComplete from "@/driver/components/InfiniteTraderAndLocationAutoComplete.vue";
import ChooseTraderButton from "@/driver/components/ChooseTraderButton.vue";
import ChooseEmissionButton from "@/driver/components/ChooseEmissionButton.vue";
import InfiniteBillingAutoComplete from "@/driver/components/InfiniteBillingAutoComplete.vue";

export default {
  name: 'DriverSalesTransactions',
  components: {
    InfiniteBillingAutoComplete,
    ChooseEmissionButton,
    ChooseTraderButton,
    InfiniteTraderAndLocationAutoComplete,
    InfiniteEmissionAndLocationAutoComplete,
    CustomPagination,
    Confirm,
    DuplicateTransactionDialog,
    ChooseBillingAddressButton,
  },
  mixins: [
    DownloadMixin,
  ],
  props: {},
  data() {
    return {
      page: 1,
      perPage: 50,
      totalVisible: 7,
      enableDuplicateDialog: false,
      conditions: {
        /* ステータス */
        status: null,
        /* 取引日(from) */
        trading_start_date: null,
        /* 取引日(to) */
        trading_end_date: null,
        /* 現掛区分 */
        receivable_type: null,
        /* 営業担当者 */
        sales_staff: null,
        /* 取引先(排出)事業者ID */
        supplier_id: null,
        /* 取引先(排出)事業者区分 */
        supplier_type: "Emission",
        /* 取引先(排出)事業場ID */
        supplier_location_id: null,
        /* 取引先(排出)事業場コード */
        supplier_location_code: null,
        /* 取引先(収運・処分)事業者ID */
        supplier_trader_id: null,
        /* 取引先(収運・処分)事業者区分 */
        supplier_trader_type: "Trader",
        /* 取引先(収運・処分)事業場ID */
        supplier_trader_location_id: null,
        /* 取引先(収運・処分)事業場コード */
        supplier_trader_location_code: null,
        /* 請求先ID */
        billing_id: null,
        /* 取引番号・引取搬入番号, 受注番号 */
        keyword: null,
        /* 表示順 */
        sort_type: "updated_date_desc"
      },
      deals: [],
      /** ステータス */
      status: [
        { value: 'unregistered', text: '売上未登録' },
        { value: 'provisional', text: '仮保存' },
        { value: 'registered', text: '登録済' },
        { value: 'final_closing', text: '本締め' },
      ],
      /** 現掛区分 */
      receivableTypes: [
        { value: 'credit_transaction', text: '掛取引'},
        { value: 'cash_transaction', text: '現金取引'},
      ],
      tableHeaders: [
        { text: 'ステータス', value: 'status', width: '5%' },
        { text: '取引番号\r\n受注番号', value: 'deal_no', align: 'center', width: '10%' },
        { text: '取引日\r\n売上計上日', value: 'trading_date', align: 'center', width: '10%' },
        { text: '取引先（事業者）\r\n取引先（事業場）', value: 'supplier_name', width: '21%' },
        { text: '請求先', value: 'billing_address', width: '21%' },
        { text: '現掛区分', value: 'receivable_type', align: 'center', width: '5%' },
        { text: '合計金額', value: 'total_amount', align: 'right', width: '9%' },
        { text: '営業担当者', value: 'sales_staff', align: 'center', width: '14%' },
        { text: '編集', value: 'editable', align: 'end', width: '5%' },
      ],
      /** 表示順 */
      sortTypes: [
        { value: 'updated_date_desc', text: '更新日（降順）▼' },
        { value: 'updated_date_asc', text: '更新日（昇順）▲' },
        { value: 'trading_date_desc', text: '取引日（降順）▼' },
        { value: 'trading_date_asc', text: '取引日（昇順）▲' },
      ],
    }
  },
  computed: {
    ...mapState('DriverSalesDeal', {
      pagination: 'pagination',
      searchCondition: 'condition',
    }),
  },
  watch: {},
  created() {
    if (this.$route.params.resetCondition) {
      this.resetState()
    }
    if (this.$route.params.resetPagination) {
      this.resetPagination()
    }
    Object.assign(this.conditions, this.searchCondition)
    this.page = this.pagination.current_page
    this.perPage = this.searchCondition.limit

    this.searchTransactions()
  },
  methods: {
    ...mapActions('DriverSalesDeal', {
      fetchDeals: 'index',
      deleteDeal: 'destroy',
      resetState: 'resetState',
      resetPagination: 'resetPagination',
    }),
    ...mapActions('DriverSettingDeal', {
      download: 'index',
    }),
    ...mapActions('DriverSalesDealOutput', {
      output_pdf: 'update',
    }),
    /**
     * 回収先（排出事業者・事業場） チェンジコールバック
     *
     * @param emission_id { Number } 排出事業者ID
     * @param location_id { Number } 排出事業場ID
     * @param location_code { String } 排出事業場コード
     */
    supplierChangeCallback (emission_id, location_id, location_code) {
      let changed = false;
      if (this.conditions.supplier_id !== emission_id) {
        changed = true;
      }

      if (this.conditions.supplier_location_id !== location_id) {
        changed = true;
      }

      this.conditions.supplier_id = emission_id;
      this.conditions.supplier_location_id = location_id;
      this.conditions.supplier_location_code = location_code;

      if (changed) {
        this.$nextTick(() => { this.searchTransactions(); })
      }
    },
    /**
     * 取引先選択ボタンからのダイアログのコールバック
     * @param item
     */
    choseSupplierCallback (item) {
      this.conditions.supplier_id = item.id;
      this.conditions.supplier_location_id = null;
      this.conditions.supplier_location_code = null;
      this.$nextTick(() => { this.searchTransactions(); })
    },
    supplierTraderChangedCallback (trader_id, location_id, location_code) {
      let changed = false;
      if (this.conditions.supplier_trader_id !== trader_id) {
        changed = true;
      }

      if (this.conditions.supplier_trader_location_id !== location_id) {
        changed = true;
      }

      this.conditions.supplier_trader_id = trader_id;
      this.conditions.supplier_trader_location_id = location_id;
      this.conditions.supplier_trader_location_code = location_code;

      if (changed) {
        this.$nextTick(() => { this.searchTransactions(); })
      }

    },
    choseSupplierTraderCallback (item) {
      this.conditions.supplier_trader_id = item.id;
      this.conditions.supplier_trader_location_id = null;
      this.conditions.supplier_trader_location_code = null;
      this.$nextTick(() => { this.searchTransactions(); })
    },
    billingChangedCallback (billing_id) {

      let changed = false;

      if (this.conditions.billing_id !== billing_id) {
        changed = true;
      }

      this.conditions.billing_id = billing_id;
      if (changed) {
        this.$nextTick(() => { this.searchTransactions(); })
      }
    },
    /**
     * 請求先選択ボタンからのダイアログのコールバック
     * @param item
     */
    choseBillingCallback (item) {
      this.conditions.billing_id = item.id;
      this.$nextTick(() => { this.searchTransactions(); })
    },

    changePage(page) {
      this.page = page;
      this.searchTransactions()
    },
    changePerPage (perPage) {
      this.perPage = perPage
      this.page = 1
      this.searchTransactions()
    },
    /**
     * 検索ボタン クリックイベント
     */
    searchButtonClickEvent () {
      this.page = 1
      this.searchTransactions()
    },
    /**
     * 新規作成ボタン クリックイベント
     * @description 新規画面に遷移する
     */
    addButtonClickEvent () {
      this.$router.push({name: 'DriverSalesTransactionNew'})
    },
    /**
     * 詳細を参照ボタン クリックイベント
     * @param item { object }
     * @description 詳細画面に遷移する
     */
    showButtonClickEvent (item) {
      this.$router.push({name: 'DriverSalesTransaction', params: { id: item.id }})
    },
    /**
     * 編集ボタン クリックイベント
     * @param item { object }
     * @description 編集画面に遷移する
     */
    editButtonClickEvent (item) {
      this.$router.push({name: 'DriverSalesTransactionEdit', params: { id: item.id }})
    },
    /**
     * 売上計上しないボタン クリックイベント
     * @param item { object }
     * @description 売上情報を削除する.
     */
    deleteButtonClickEvent (item) {
      this.$refs.confirm.showDeleteConfirm().then((confirm) => {
        this.$refs.confirm.close()
          if (confirm) {
            this.deleteDeal({id: item.id}).then(() => {
              this.searchTransactions()
            })
          }
      })
    },
    viewVoucherPdfButtonClickEvent (item) {
      // window.open(item.voucher_url, "dealPdfTab");
      this.output_pdf({ id: item.id }).then((content) => {
        if (content.voucher_url) {
          window.open(content.voucher_url, "dealPdfTab");
        }
      })
    },
    searchTransactions () {
      const params = this.buildParam('json')
      this.fetchDeals({params: params}).then((content) => {
        this.deals = content.deals
      })
    },

    showDuplicateDialogButtonClickEvent () {
      this.enableDuplicateDialog = true
    },
    duplicateDialogCloseCallback () {
      this.enableDuplicateDialog = false
    },
    duplicateDialogRegisterCallback(transaction_id) {
      this.duplicateDialogCloseCallback()
      this.$router.push({name: 'DriverSalesTransactionDuplicate', params: { duplicate_id: transaction_id }})
    },

    /**
     * 検索パラメータの生成
     * @param format
     * @return {{page: number, limit: number, format: *}}
     */
    buildParam (format) {
      return {
        ...this.conditions,
        page: this.page,
        limit: this.perPage,
        format: format
      }
    },
    /**
     * CSV出力ボタン クリックイベント
     */
    outputCsvButtonClickEvent () {
      let params = this.buildParam('csv')
      this.download(params).then((content) => {
        this.downloadCsv(content, '売上管理')
      })
    },
    /**
     * 受注番号 クリックイベント
     */
    orderNoClickEvent (item) {
      const params = { order_no: item.order_no }
      this.$router.push( { name: 'DriverOrderAccepts', params: params } )
    },
    /**
     * 取引日（from）変更　コールバック
     */
    tradingStartDateChangedCallback (value) {
      this.conditions.trading_start_date = value;
      this.searchButtonClickEvent();
    },
    tradingStartDateClearEvent () {
      this.conditions.trading_start_date = null;
      this.searchButtonClickEvent();
    },
    /**
     * 取引日（to）変更　コールバック
     */
    tradingEndDateChangedCallback (value) {
      this.conditions.trading_end_date = value;
      this.searchButtonClickEvent();
    },
    tradingEndDateClearEvent() {
      this.conditions.trading_end_date = null;
      this.searchButtonClickEvent();
    },
    /**
     * 取引番号・引取搬入番号・受注番号で検索
     */
    keywordClearEvent () {
      this.conditions.keyword = null;
      this.searchButtonClickEvent();
    },
  }
}
</script>


<style scoped lang="scss">

.panel-header-color {
  color: #0D74BA !important;
  & .v-expansion-panel-header__icon {
    & .v-icon {
      color: #0D74BA !important;
      &::before {
        color: #0D74BA !important;
      }
    }
  }
}
.panel-selectable {
  background-color: #DCE7EB;
}

</style>
